import configureIcon from "@assets/images/menu-configure-smop-icon.svg";
import { Tooltip } from "@material-ui/core";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import { isSuperUser } from "@saleor/auth/misc";
import { User } from "@saleor/fragments/types/User";
import useTheme from "@saleor/hooks/useTheme";
import { PermissionEnum } from "@saleor/types/globalTypes";
import classNames from "classnames";
import React, { Fragment } from "react";
import SVG from "react-inlinesvg";
import { useIntl } from "react-intl";
import { useMatch } from "react-router-dom";

import { createConfigurationMenu } from "../../configuration";
import { createHref } from "../../misc";
import MenuNested from "./MenuNested";
import {
  filterMenuStructure,
  IMenuItem,
  isActiveMenuItemUrl
} from "./menuStructure";

const useStyles = makeStyles(
  theme => ({
    menuIcon: {
      "& svg": {
        height: 25,
        position: "relative",
        top: "-5px",
        width: 25
      },
      display: "inline-block",
      position: "relative",
      top: 8
    },
    menuIconDark: {
      "& path": {
        fill: theme.palette.common.white
      }
    },
    menuIconSmall: {
      left: -2
    },
    menuIsActive: {
      boxShadow: "0px 0px 12px 1px rgba(0,0,0,0.2)"
    },
    menuItemHover: {
      "& p": {
        fontSize: 14,
        transition: "color 0.5s ease, opacity 0.3s ease-out"
      },
      "& path": {
        transition: "fill 0.5s ease"
      },
      "&:hover": {
        "& p": {
          color: theme.palette.primary.main
        },
        "&:before": {
          borderLeft: `solid 2px ${theme.palette.primary.main}`,
          content: "''",
          height: 33,
          left: -20,
          position: "absolute",
          top: 8
        },
        color: theme.palette.primary.main
      },
      cursor: "pointer",
      position: "relative"
    },
    menuList: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      marginLeft: theme.spacing(4),
      marginTop: theme.spacing(2),
      paddingBottom: theme.spacing(3)
    },
    menuListItem: {
      alignItems: "center",
      borderRadius: "5px",
      display: "block",
      marginBottom: "5px",
      paddingBottom: "5px",
      paddingLeft: "8px",
      paddingTop: "5px",
      textDecoration: "none",
      transition: theme.transitions.duration.standard + "ms"
    },
    menuListItemActive: {
      "& $menuListItem": {
        backgroundColor: "#DCE4FF"
      },
      "& $menuListItemText": {
        color: theme.palette.primary.main
      },
      "& path": {
        color: theme.palette.primary.main
      },
      backgroundColor: "#DCE4FF"
    },
    menuListItemOpen: {
      "&:after": {
        borderBottom: `10px solid transparent`,
        borderLeft: `10px solid ${theme.palette.background.paper}`,
        borderTop: `10px solid transparent`,
        content: "''",
        height: 0,
        position: "absolute",
        right: -30,
        top: 15,
        width: 0
      },
      "&:before": {
        borderLeft: `solid 2px ${theme.palette.primary.main}`,
        content: "''",
        height: 33,
        left: -20,
        position: "absolute",
        top: 8
      },
      position: "relative"
    },
    menuListItemText: {
      "&:hover": {
        color: theme.palette.primary.main
      },
      bottom: 0,
      cursor: "pointer",
      fontSize: "1rem",
      fontWeight: 500,
      left: 30,
      opacity: 1,
      paddingBottom: 2,
      paddingLeft: 10,
      position: "absolute",
      textTransform: "uppercase",
      transition: "opacity 0.5s ease"
    },
    menuListItemTextHide: {
      bottom: 0,
      left: 30,
      opacity: 0,
      position: "absolute"
    },
    subMenu: {
      padding: "0 15px"
    },
    subMenuDrawer: {
      background: "#000",
      cursor: "pointer",
      height: "100vh",
      left: 0,
      opacity: 0.2,
      position: "absolute",
      top: 0,
      width: 0,
      zIndex: -2
    },
    subMenuDrawerOpen: {
      width: `100vw`
    }
  }),
  { name: "MenuList" }
);

const TooltipWithArrow = withStyles(
  {
    tooltip: {
      "&:after": {
        borderColor: "transparent #2b3039 transparent transparent",
        borderStyle: "solid",
        borderWidth: 5,
        content: "' '",
        marginTop: -5,
        position: "absolute",
        right: "100%",
        top: "50%"
      },
      backgroundColor: "#2b3039",
      fontSize: "1em",
      left: 20,
      marginLeft: 0,
      position: "relative"
    }
  },
  { name: "TooltipWithArrow" }
)(Tooltip);

interface MenuListProps {
  className?: string;
  menuItems: IMenuItem[];
  isMenuSmall: boolean;
  location: string;
  user: User;
  renderConfigure: boolean;
  onMenuItemClick: (url: string, event: React.MouseEvent<any>) => void;
}

export interface IActiveSubMenu {
  isActive: boolean;
  label: string | null;
}

function MenuItemWrapper({ children, isMenuSmall, itemLabel, contextId }) {
  const [openTooltip, setOpenTooltip] = React.useState(false);
  const handleOpenTooltip = () => {
    if (!isMenuSmall) {
      setOpenTooltip(true);
    }
  };

  return (
    <TooltipWithArrow
      id={`${contextId}Tooltip`}
      title={itemLabel}
      placement="right"
      open={openTooltip}
      onOpen={handleOpenTooltip}
      onClose={() => setOpenTooltip(false)}
    >
      {children}
    </TooltipWithArrow>
  );
}

const MenuList: React.FC<MenuListProps> = props => {
  const {
    className,
    menuItems,
    isMenuSmall,
    location,
    user,
    renderConfigure,
    onMenuItemClick
  } = props;
  const match = useMatch(location.split("?")[0]);

  const classes = useStyles(props);

  const { isDark } = useTheme();
  const [activeSubMenu, setActiveSubMenu] = React.useState<IActiveSubMenu>({
    isActive: false,
    label: null
  });
  const intl = useIntl();

  const configMenuItem = {
    ariaLabel: "ConfigurationTab",
    children: [
      {
        ariaLabel: "global",
        label: "Global",
        permission: PermissionEnum.MANAGE_APP_CONFIGS,
        testingContextId: "configGlobal",
        url: "/configuration"
      },
      {
        ariaLabel: "operations",
        label: "Operations",
        permissions: [
          PermissionEnum.MANAGE_REMITTANCE_CONFIG,
          PermissionEnum.MANAGE_AUTO_BOOKING
        ],
        testingContextId: "configOperations",
        url: "/operations"
      }
    ],
    icon: configureIcon,
    label: "Configuration",
    testingContextId: "Configuration"
  };
  const configurationMenu = createConfigurationMenu(intl).map(menu => {
    menu.menuItems.forEach(item =>
      user.userPermissions.map(perm => perm.code).includes(item.permission)
    );
  });

  const handleSubMenu = itemLabel => {
    setActiveSubMenu({
      isActive:
        itemLabel === activeSubMenu.label ? !activeSubMenu.isActive : true,
      label: itemLabel
    });
  };

  const closeSubMenu = (menuItemUrl, event) => {
    setActiveSubMenu({
      isActive: false,
      label: null
    });
    if (menuItemUrl && event) {
      onMenuItemClick(menuItemUrl, event);
      event.stopPropagation();
      event.preventDefault();
    }
  };

  return (
    <div
      className={classNames(className, {
        [classes.menuIsActive]: activeSubMenu.isActive
      })}
      data-test="mainMenu"
    >
      {filterMenuStructure(menuItems).map(menuItem => {
        const isActive = (menuItem: IMenuItem) =>
          menuItem.url && isActiveMenuItemUrl(menuItem.url, location)
            ? match
            : false;

        if (
          menuItem.excludeFrom &&
          menuItem.excludeFrom.some(permission =>
            user.userPermissions.map(perm => perm.code).includes(permission)
          ) &&
          !isSuperUser(user)
        ) {
          return null;
        }

        if (!menuItem.url) {
          const isAnyChildActive = menuItem.children.reduce(
            (acc, child) => acc || isActive(child),
            false
          );

          return (
            <div
              className={classNames(classes.menuListItem, {
                [classes.menuListItemActive]: isAnyChildActive
              })}
              key={menuItem.label}
              data-testid={menuItem.testingContextId}
              data-test="menuItemEntry"
            >
              <MenuItemWrapper
                isMenuSmall={isMenuSmall}
                itemLabel={menuItem.label}
                contextId={menuItem.testingContextId}
              >
                <div
                  className={classNames(classes.menuItemHover, {
                    [classes.menuListItemOpen]:
                      menuItem.ariaLabel === activeSubMenu.label &&
                      activeSubMenu.isActive
                  })}
                  onClick={() => handleSubMenu(menuItem.ariaLabel)}
                >
                  <SVG
                    className={classNames(classes.menuIcon, {
                      [classes.menuIconDark]: isDark,
                      [classes.menuIconSmall]: !isMenuSmall
                    })}
                    src={menuItem.icon}
                  />
                  <Typography
                    aria-label={menuItem.ariaLabel}
                    className={classNames(classes.menuListItemText, {
                      [classes.menuListItemTextHide]: !isMenuSmall
                    })}
                  >
                    {menuItem.label}
                  </Typography>
                </div>
              </MenuItemWrapper>
              <MenuNested
                activeItem={activeSubMenu}
                closeSubMenu={setActiveSubMenu}
                menuItem={menuItem}
                onMenuItemClick={onMenuItemClick}
                handleSubMenu={handleSubMenu}
                title={menuItem.label}
                icon={menuItem.icon}
                ariaLabel={menuItem.ariaLabel}
              />
              <div
                onClick={event => closeSubMenu(null, event)}
                className={classNames(classes.subMenuDrawer, {
                  [classes.subMenuDrawerOpen]: activeSubMenu.isActive
                })}
              />
            </div>
          );
        }

        return (
          <a
            className={classNames(classes.menuListItem, {
              [classes.menuListItemActive]: isActive(menuItem)
            })}
            href={createHref(menuItem.url)}
            onClick={event => closeSubMenu(menuItem.url, event)}
            key={menuItem.label}
            data-testid={menuItem.testingContextId}
            data-test="menuItemEntry"
          >
            <MenuItemWrapper
              isMenuSmall={isMenuSmall}
              itemLabel={menuItem.label}
              contextId={menuItem.testingContextId}
            >
              <div className={classes.menuItemHover}>
                <SVG
                  className={classNames(classes.menuIcon, {
                    [classes.menuIconDark]: isDark,
                    [classes.menuIconSmall]: !isMenuSmall
                  })}
                  src={menuItem.icon}
                />
                <Typography
                  aria-label={menuItem.ariaLabel}
                  className={classNames(classes.menuListItemText, {
                    [classes.menuListItemTextHide]: !isMenuSmall
                  })}
                >
                  {menuItem.label}
                </Typography>
              </div>
            </MenuItemWrapper>
          </a>
        );
      })}
      {user &&
      user.accessGroup &&
      user.accessGroup.smopUsertypeCode === "ACQUISITION" ? (
        ""
      ) : (
        <Fragment>
          {renderConfigure &&
            configurationMenu.length > 0 &&
            filterMenuStructure(configMenuItem.children).length > 0 && (
              <div
                className={classNames(classes.menuListItem)}
                key={configMenuItem.label}
                data-testid={configMenuItem.testingContextId}
                data-test="menuItemEntry"
              >
                <MenuItemWrapper
                  isMenuSmall={isMenuSmall}
                  itemLabel={configMenuItem.label}
                  contextId={configMenuItem.testingContextId}
                >
                  <div
                    className={classNames(classes.menuItemHover, {
                      [classes.menuListItemOpen]:
                        configMenuItem.ariaLabel === activeSubMenu.label &&
                        activeSubMenu.isActive
                    })}
                    onClick={() => handleSubMenu(configMenuItem.ariaLabel)}
                  >
                    <SVG
                      className={classNames(classes.menuIcon, {
                        [classes.menuIconDark]: isDark,
                        [classes.menuIconSmall]: !isMenuSmall
                      })}
                      src={configMenuItem.icon}
                    />
                    <Typography
                      aria-label={configMenuItem.ariaLabel}
                      className={classNames(classes.menuListItemText, {
                        [classes.menuListItemTextHide]: !isMenuSmall
                      })}
                    >
                      {configMenuItem.label}
                    </Typography>
                  </div>
                </MenuItemWrapper>
                <MenuNested
                  activeItem={activeSubMenu}
                  closeSubMenu={setActiveSubMenu}
                  menuItem={configMenuItem}
                  onMenuItemClick={onMenuItemClick}
                  handleSubMenu={handleSubMenu}
                  title={configMenuItem.label}
                  icon={configMenuItem.icon}
                  ariaLabel={configMenuItem.ariaLabel}
                />
                <div
                  onClick={event => closeSubMenu(null, event)}
                  className={classNames(classes.subMenuDrawer, {
                    [classes.subMenuDrawerOpen]: activeSubMenu.isActive
                  })}
                />
              </div>
            )}
        </Fragment>
      )}
    </div>
  );
};

MenuList.displayName = "MenuList";
export default MenuList;
